import { invoke } from '~/lib/tauriInvoke'
import { runAction } from '~/stores/storeErrorHandling'

export { BotType, AiProvider, BotReplyScope } from '~/types/bots'
export type {
  Bot,
  AgentCredential,
  BotResponse,
  BotReplyPolicy,
  UpdateBotReplyPolicyRequest,
  CreateBotRequest,
  CreateBotResponse,
  CreateAiCredentialRequest,
  CreateAiCredentialResponse,
  UpdateBotRequest,
  AddBotToGroupResponse,
  BotIntentInfo,
} from '~/types/bots'

// Module-level (singleton) state: `bots/index.vue` gets torn down and recreated every time you
// navigate away and back (it's a page, not a persisted store), so per-call `ref`s here would
// re-fetch on every visit. Hoisting these out of `useBots()` — same pattern as `useCallOverlay`'s
// singleton ref — makes the list survive navigation and lets `getMyBots()` skip the network call
// once it's already been fetched.
const bots = ref<Bot[]>([])
const isLoading = ref(false)
const error = ref<string | null>(null)
const hasFetchedBots = ref(false)
// Reply policy per bot id. Not part of `Bot` (only `GetBot`/`UpdateBotReplyPolicy` return it), so
// it's cached separately — filled on first open of a bot, updated from every save.
const replyPolicies = ref<Record<string, BotReplyPolicy>>({})

/** Logout: the next account must not see this one's bots (see `clearUserSession`). */
export function resetBots() {
  bots.value = []
  isLoading.value = false
  error.value = null
  hasFetchedBots.value = false
  replyPolicies.value = {}
}

export function useBots() {
  const noopSetError = () => {}

  // `force: true` bypasses the cache — used after actions elsewhere that could invalidate the
  // list out from under this page (there currently are none, but keeps this from silently going
  // stale if one is added later without remembering to flip a flag here).
  async function getMyBots(force = false): Promise<Bot[]> {
    if (hasFetchedBots.value && !force) return bots.value
    isLoading.value = true
    error.value = null
    try {
      const result = (await runAction(
        (msg) => (error.value = msg),
        { action: 'get_my_bots' },
        async () => {
          const result = await invoke<Bot[]>('get_my_bots')
          bots.value = result
          return result
        },
      )) as Bot[]
      hasFetchedBots.value = true
      return result
    } finally {
      isLoading.value = false
    }
  }

  function applyBotResponse(response: BotResponse) {
    replyPolicies.value[response.bot.id] = response.reply_policy
    const index = bots.value.findIndex((b) => b.id === response.bot.id)
    if (index !== -1) bots.value[index] = response.bot
  }

  async function getBot(botId: string): Promise<BotResponse> {
    return runAction(noopSetError, { action: 'get_bot', extra: { botId } }, async () => {
      const response = await invoke<BotResponse>('get_bot', { botId })
      applyBotResponse(response)
      return response
    }) as Promise<BotResponse>
  }

  // Saves only the policy — the server leaves every other bot field alone.
  async function updateBotReplyPolicy(request: UpdateBotReplyPolicyRequest): Promise<BotResponse> {
    return runAction(noopSetError, { action: 'update_bot_reply_policy', extra: { botId: request.bot_id } }, async () => {
      const response = await invoke<BotResponse>('update_bot_reply_policy', { payload: request })
      applyBotResponse(response)
      return response
    }) as Promise<BotResponse>
  }

  async function createBot(request: CreateBotRequest): Promise<CreateBotResponse> {
    return runAction(noopSetError, { action: 'create_bot' }, async () => {
      const response = await invoke<CreateBotResponse>('create_bot', { payload: request })
      bots.value.push(response.bot)
      return response
    }) as Promise<CreateBotResponse>
  }

  async function createAiCredential(request: CreateAiCredentialRequest): Promise<CreateAiCredentialResponse> {
    return runAction(noopSetError, { action: 'create_ai_credential' }, async () => {
      const response = await invoke<CreateAiCredentialResponse>('create_ai_credential', { payload: request })
      const index = bots.value.findIndex((b) => b.id === response.bot.id)
      if (index !== -1) bots.value[index] = response.bot
      return response
    }) as Promise<CreateAiCredentialResponse>
  }

  async function updateBot(request: UpdateBotRequest): Promise<Bot> {
    return runAction(noopSetError, { action: 'update_bot' }, async () => {
      const updated = await invoke<Bot>('update_bot', { payload: request })
      const index = bots.value.findIndex((b) => b.id === updated.id)
      if (index !== -1) bots.value[index] = updated
      return updated
    }) as Promise<Bot>
  }

  async function deleteBot(botId: string): Promise<void> {
    await runAction(noopSetError, { action: 'delete_bot', extra: { botId } }, async () => {
      await invoke('delete_bot', { botId })
      bots.value = bots.value.filter((b) => b.id !== botId)
    })
  }

  async function regenerateBotToken(botId: string): Promise<string> {
    return runAction(noopSetError, { action: 'regenerate_bot_token', extra: { botId } }, () =>
      invoke<string>('regenerate_bot_token', { botId }),
    ) as Promise<string>
  }

  async function addBotToGroup(botId: string, groupId: string, permissions = 0): Promise<AddBotToGroupResponse> {
    return runAction(noopSetError, { action: 'add_bot_to_group' }, () =>
      invoke<AddBotToGroupResponse>('add_bot_to_group', {
        payload: { bot_id: botId, group_id: groupId, permissions },
      }),
    ) as Promise<AddBotToGroupResponse>
  }

  async function getAvailableIntents(): Promise<BotIntentInfo[]> {
    return runAction(noopSetError, { action: 'get_available_intents' }, () =>
      invoke<BotIntentInfo[]>('get_available_intents'),
    ) as Promise<BotIntentInfo[]>
  }

  // Only the bot's creator can call this (enforced server-side in UserBotActor - a non-owner
  // request fails as "Bot not found", same as every other owner-scoped bot RPC). Same
  // initiate/PUT-to-S3/confirm flow as `uploadGroupAvatar`/`useAuth.ts`'s own-profile upload.
  async function uploadBotAvatar(botId: string, file: File): Promise<Bot> {
    return runAction(noopSetError, { action: 'upload_bot_avatar', extra: { botId } }, async () => {
      const contentType = file.type || 'application/octet-stream'
      const initiate = await invoke<{ media_id: string; upload_url: string; expires_at?: string }>(
        'initiate_bot_avatar_upload',
        { botId, contentType, sizeBytes: file.size },
      )

      const uploadResponse = await fetch(initiate.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      })
      if (!uploadResponse.ok) throw new Error(`S3 upload failed with status ${uploadResponse.status}`)

      const updated = await invoke<Bot>('confirm_bot_avatar_upload', { botId, avatarId: initiate.media_id })
      const index = bots.value.findIndex((b) => b.id === updated.id)
      if (index !== -1) bots.value[index] = updated
      return updated
    }) as Promise<Bot>
  }

  async function deleteBotAvatar(botId: string): Promise<Bot> {
    return runAction(noopSetError, { action: 'delete_bot_avatar', extra: { botId } }, async () => {
      const updated = await invoke<Bot>('delete_bot_avatar', { botId })
      const index = bots.value.findIndex((b) => b.id === updated.id)
      if (index !== -1) bots.value[index] = updated
      return updated
    }) as Promise<Bot>
  }

  return {
    bots,
    isLoading,
    error,
    getMyBots,
    getBot,
    replyPolicies,
    updateBotReplyPolicy,
    createBot,
    createAiCredential,
    updateBot,
    deleteBot,
    regenerateBotToken,
    addBotToGroup,
    getAvailableIntents,
    uploadBotAvatar,
    deleteBotAvatar,
  }
}
